import {Button, Modal, Table, Tag} from 'antd'
import {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {casinosConfig, providersConfig} from './api'

type BranchTagRow = { key: string; branch: string; tags: string[] }

// Kind selects which settings the widget lists and which page a row click opens.
type BranchTagsKind = 'casino' | 'provider'

type BranchTagsWidgetProps = {
  open: boolean
  onClose: () => void
  kind?: BranchTagsKind
}

const kindConfig: Record<BranchTagsKind, {
  load: () => Promise<{branches: Record<string, {tags: string[]}>}>
  route: string
}> = {
  casino: {
    load: () => casinosConfig.settingsBranches({}),
    route: '/casino_setting',
  },
  provider: {
    load: () => providersConfig.settingsBranches({}),
    route: '/provider_setting',
  },
}

// BranchTagsWidget shows a modal listing branches that have tags.
// Open state is controlled by the parent via props (the trigger button lives there).
// Clicking a row navigates to the casino/provider settings page for that branch.
export default function BranchTagsWidget({open, onClose, kind = 'casino'}: BranchTagsWidgetProps) {
  const navigate = useNavigate()
  const [rows, setRows] = useState([] as BranchTagRow[])

  const load = () =>
    kindConfig[kind].load()
      .then((resp) => {
        const branches = resp.branches ?? {}
        const tagged = Object.entries(branches)
          .filter(([, info]) => (info.tags ?? []).length > 0)
          .map(([branch, info]) => ({key: branch, branch, tags: info.tags ?? []}))
        setRows(tagged)
        return tagged
      })
      .catch((e) => { console.error('settingsBranches error', e); return [] as BranchTagRow[] })

  useEffect(() => {
    // Refresh the list whenever the parent opens the modal
    // and close it right away when no branch is tagged.
    if (!open) return
    load().then((tagged) => { if (tagged.length === 0) onClose() })
  }, [open])

  return (
      <Modal
        title="Branch tags"
        open={open && rows.length > 0}
        onCancel={onClose}
        footer={<Button onClick={onClose}>Close</Button>}
      >
        <Table
          size="small"
          pagination={false}
          rowKey={(r) => r.key}
          dataSource={rows}
          onRow={(r) => ({
            onClick: () => {
              onClose()
              navigate(`${kindConfig[kind].route}?branch=${encodeURIComponent(r.branch)}`)
            },
            style: {cursor: 'pointer'},
          })}
          columns={[
            {title: 'branch', dataIndex: 'branch', key: 'branch'},
            {
              title: 'tags',
              dataIndex: 'tags',
              key: 'tags',
              render: (tags: string[]) => tags.map((t) => <Tag key={t}>{t}</Tag>),
            },
          ]}
        />
      </Modal>
  )
}